import type { RenderTask } from 'pdfjs-dist/webpack.mjs';
import type { IAnnotation, IArea, IDrawing } from '@datatype/gridgio/type';
import { acquirePdfDocument } from '@util/pdf-document-cache';

const PDF_RENDER_SCALE = 2;
const MAX_PAGE_SIZE = 1440;
const ANNOTATION_COLOR = '#ff3b30';

interface IFileSystemWritableFileStream {
  write: (data: Blob) => Promise<void>;
  close: () => Promise<void>;
}

interface IFileSystemFileHandle {
  createWritable: () => Promise<IFileSystemWritableFileStream>;
}

type TSaveFilePicker = (options: {
  suggestedName: string;
  types: { description: string; accept: Record<string, string[]> }[];
}) => Promise<IFileSystemFileHandle>;

const getVisibleArea = (area: IArea | null) => {
  if (!area) return { left: 0, top: 0, width: 1, height: 1 };
  const x = area.point.map((point) => point.x);
  const y = area.point.map((point) => point.y);
  const left = Math.min(...x);
  const top = Math.min(...y);
  return { left, top, width: Math.max(...x) - left, height: Math.max(...y) - top };
};

type TAreaBounds = ReturnType<typeof getVisibleArea>;

const createCanvas = (width: number, height: number) => {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  return canvas;
};

const loadImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('도면 이미지를 불러오지 못했습니다.'));
    image.src = url;
  });

const renderImage = async (drawing: IDrawing, rotation: number, area: TAreaBounds) => {
  const image = await loadImage(drawing.url);
  const isQuarterTurn = rotation % 180 !== 0;
  const sourceWidth = isQuarterTurn ? image.naturalHeight : image.naturalWidth;
  const sourceHeight = isQuarterTurn ? image.naturalWidth : image.naturalHeight;
  const canvas = createCanvas(sourceWidth * area.width, sourceHeight * area.height);
  const context = canvas.getContext('2d');
  if (!context) throw new Error('내보내기 Canvas를 생성하지 못했습니다.');

  context.translate(-area.left * sourceWidth, -area.top * sourceHeight);
  if (rotation === 90) {
    context.translate(sourceWidth, 0);
    context.rotate(Math.PI / 2);
  } else if (rotation === 180) {
    context.translate(sourceWidth, sourceHeight);
    context.rotate(Math.PI);
  } else if (rotation === 270) {
    context.translate(0, sourceHeight);
    context.rotate(-Math.PI / 2);
  }
  context.drawImage(image, 0, 0);
  return canvas;
};

const renderPdf = async (drawing: IDrawing, rotation: number, area: TAreaBounds) => {
  const pdfHandle = acquirePdfDocument(drawing.url);
  let renderTask: RenderTask | null = null;
  try {
    const pdf = await pdfHandle.document;
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: PDF_RENDER_SCALE, rotation });
    const canvas = createCanvas(viewport.width * area.width, viewport.height * area.height);
    const context = canvas.getContext('2d');
    if (!context) throw new Error('내보내기 Canvas를 생성하지 못했습니다.');
    renderTask = page.render({
      canvas,
      canvasContext: context,
      viewport,
      transform: [1, 0, 0, 1, -area.left * viewport.width, -area.top * viewport.height],
    });
    await renderTask.promise;
    return canvas;
  } finally {
    renderTask?.cancel();
    pdfHandle.release();
  }
};

const drawAnnotation = (canvas: HTMLCanvasElement, annotation: IAnnotation[], area: TAreaBounds) => {
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Annotation을 합성하지 못했습니다.');

  context.strokeStyle = ANNOTATION_COLOR;
  context.lineWidth = Math.max(2, Math.min(canvas.width, canvas.height) * 0.004);
  context.lineCap = 'round';
  context.lineJoin = 'round';

  annotation.forEach(({ mark }) => {
    const markStroke = mark.stroke.map(({ point }) => point);
    markStroke.forEach((point) => {
      if (point.length < 2) return;
      context.beginPath();
      point.forEach((position, index) => {
        const x = ((position.x - area.left) / area.width) * canvas.width;
        const y = ((position.y - area.top) / area.height) * canvas.height;
        if (index === 0) context.moveTo(x, y);
        else context.lineTo(x, y);
      });
      context.stroke();
    });
  });
};

const canvasToPng = (canvas: HTMLCanvasElement) =>
  new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('PDF 이미지를 생성하지 못했습니다.'))),
      'image/png',
    ),
  );

const appendCanvasToPdf = async (pdf: import('pdf-lib').PDFDocument, canvas: HTMLCanvasElement) => {
  const png = await canvasToPng(canvas);
  const embeddedImage = await pdf.embedPng(await png.arrayBuffer());
  const pageScale = Math.min(1, MAX_PAGE_SIZE / Math.max(canvas.width, canvas.height));
  const page = pdf.addPage([canvas.width * pageScale, canvas.height * pageScale]);
  page.drawImage(embeddedImage, { width: page.getWidth(), height: page.getHeight() });
};

export const createModifiedDrawingPdf = async ({
  drawing,
  rotation,
  annotation,
}: {
  drawing: IDrawing;
  rotation: number;
  workArea: IArea | null;
  annotation: Record<string, IAnnotation[]>;
}) => {
  const { PDFDocument, rgb } = await import('pdf-lib');

  if (drawing.type === 'image') {
    const outputPdf = await PDFDocument.create();
    const fullArea = getVisibleArea(null);
    const canvas = await renderImage(drawing, rotation, fullArea);
    drawAnnotation(canvas, annotation.sheet1 ?? [], fullArea);
    await appendCanvasToPdf(outputPdf, canvas);
    return new Blob([await outputPdf.save()], { type: 'application/pdf' });
  }

  const outputPdf = await PDFDocument.load(await drawing.file.arrayBuffer());
  outputPdf.getPages().forEach((page, pageIndex) => {
    const { width, height } = page.getSize();
    const getPdfPoint = ({ x, y }: { x: number; y: number }) => {
      if (rotation === 90) return { x: y * width, y: x * height };
      if (rotation === 180) return { x: (1 - x) * width, y: y * height };
      if (rotation === 270) return { x: (1 - y) * width, y: (1 - x) * height };
      return { x: x * width, y: (1 - y) * height };
    };

    (annotation[`sheet${pageIndex + 1}`] ?? []).forEach(({ mark }) => {
      const markStroke = mark.stroke.map(({ point }) => point);
      markStroke.forEach((point) => {
        for (let index = 1; index < point.length; index += 1) {
          page.drawLine({
            start: getPdfPoint(point[index - 1]),
            end: getPdfPoint(point[index]),
            thickness: Math.max(1, Math.min(width, height) * 0.004),
            color: rgb(1, 59 / 255, 48 / 255),
            opacity: 1,
          });
        }
      });
    });
  });

  return new Blob([await outputPdf.save()], { type: 'application/pdf' });
};

const getExportName = (name: string) => `${name.replace(/\.[^.]+$/, '')}-annotation.pdf`;

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};

const openPdfPreview = (blob: Blob, previewWindow: Window) => {
  const url = URL.createObjectURL(blob);
  previewWindow.location.replace(url);
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

export const exportDrawingPdf = async ({
  drawing,
  rotation,
  workArea,
  annotation,
}: {
  drawing: IDrawing;
  rotation: number;
  workArea: IArea | null;
  annotation: IAnnotation[];
}) => {
  const fileName = getExportName(drawing.name);
  const saveFilePicker = (window as typeof window & { showSaveFilePicker?: TSaveFilePicker }).showSaveFilePicker;
  const previewWindow = !window.isSecureContext && !saveFilePicker ? window.open('', '_blank') : null;
  if (previewWindow) {
    previewWindow.document.title = 'PDF 내보내기';
    previewWindow.document.body.textContent = 'PDF를 생성하고 있습니다...';
  }
  const fileHandle = saveFilePicker
    ? await saveFilePicker.call(window, {
        suggestedName: fileName,
        types: [{ description: 'PDF 문서', accept: { 'application/pdf': ['.pdf'] } }],
      })
    : null;
  const visibleArea = getVisibleArea(workArea);
  const canvas =
    drawing.type === 'pdf'
      ? await renderPdf(drawing, rotation, visibleArea)
      : await renderImage(drawing, rotation, visibleArea);
  drawAnnotation(canvas, annotation, visibleArea);

  const png = await canvasToPng(canvas);
  const { PDFDocument } = await import('pdf-lib');
  const pdf = await PDFDocument.create();
  const embeddedImage = await pdf.embedPng(await png.arrayBuffer());
  const pageScale = Math.min(1, MAX_PAGE_SIZE / Math.max(canvas.width, canvas.height));
  const page = pdf.addPage([canvas.width * pageScale, canvas.height * pageScale]);
  page.drawImage(embeddedImage, { width: page.getWidth(), height: page.getHeight() });

  const blob = new Blob([await pdf.save()], { type: 'application/pdf' });
  if (fileHandle) {
    const writable = await fileHandle.createWritable();
    await writable.write(blob);
    await writable.close();
    return;
  }
  if (previewWindow) {
    openPdfPreview(blob, previewWindow);
    return;
  }
  downloadBlob(blob, fileName);
};
